import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, Wallet, Eye, MessageSquare, Star, Plus, Users, TrendingUp,
  Clock, CheckCircle2, Hourglass, Activity,
} from "lucide-react";
import { useUser } from "../context/UserContext";
import { listOrders, listCreators, listConversations, getStats, getDashboard } from "../lib/api";
import MetricCard from "../components/MetricCard";
import StatusPill from "../components/StatusPill";
import { formatEUR, formatNumber, timeAgo } from "../lib/constants";

export default function Dashboard() {
  const { user, openChat } = useUser();
  const [orders, setOrders] = useState([]);
  const [convs, setConvs] = useState([]);
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    listOrders().then(setOrders).catch(() => setOrders([]));
    listConversations().then(setConvs).catch(() => setConvs([]));
    getStats().then(setStats).catch(() => {});
    listCreators({ sort: "recent" }).then((r) => setCreators(r.slice(0, 4))).catch(() => {});
    getDashboard().then(setDash).catch(() => setDash(null));
  }, [user?.id, user?.role]);

  const isCompany = user?.role === "company";
  const m = dash?.metrics || {};
  const activeOrders = orders.filter((o) => o.status !== "completed" && o.status !== "cancelled");

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="cc-overline">Dashboard · {isCompany ? "Company" : "Creator"}</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-2">
            Hi, <span className="text-[#00ff88]">{user?.display_name}</span>
          </h1>
        </div>
        {isCompany ? (
          <Link to="/creators" data-testid="dashboard-post-job-btn" className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm inline-flex items-center gap-2">
            <Plus size={14} /> Find a Creator
          </Link>
        ) : (
          <Link to="/wallet" data-testid="dashboard-wallet-btn" className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm inline-flex items-center gap-2">
            <Wallet size={14} /> View Wallet
          </Link>
        )}
      </div>

      {/* Real metrics from /api/dashboard */}
      {isCompany ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard testId="metric-active-orders" label="Active Orders" value={m.active_orders ?? 0} icon={Briefcase} sub={`${m.completed_orders ?? 0} completed`} />
          <MetricCard testId="metric-spent" label="Total Spent" value={formatEUR(m.total_spent_eur ?? 0)} icon={Wallet} accent sub="Released to creators" />
          <MetricCard testId="metric-escrow" label="In Escrow" value={formatEUR(m.in_escrow_eur ?? 0)} icon={Hourglass} sub="Currently locked" />
          <MetricCard testId="metric-creators-engaged" label="Creators Engaged" value={m.creators_engaged ?? 0} icon={Users} sub={`${m.open_conversations ?? 0} threads`} />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard testId="metric-available" label="Available" value={formatEUR(m.available_balance ?? 0)} icon={Wallet} accent sub="Ready to withdraw" />
          <MetricCard testId="metric-pending" label="Pending" value={formatEUR(m.pending_balance ?? 0)} icon={Hourglass} sub="In escrow" />
          <MetricCard testId="metric-active-orders" label="Active Orders" value={m.active_orders ?? 0} icon={Briefcase} sub={`${m.completed_orders ?? 0} delivered`} />
          <MetricCard
            testId="metric-rating"
            label="Avg Rating"
            value={m.reviews_count > 0 ? `${m.avg_rating.toFixed(2)} ★` : "—"}
            icon={Star}
            sub={`${m.reviews_count ?? 0} review${(m.reviews_count ?? 0) === 1 ? "" : "s"}`}
          />
        </div>
      )}

      {/* Secondary creator-only stats */}
      {!isCompany && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MiniBox icon={Eye} label="Profile Views" value={formatNumber(m.profile_views ?? 0)} testId="metric-views" />
          <MiniBox icon={Clock} label="Avg Delivery" value={m.avg_delivery_hours ? `${m.avg_delivery_hours}h` : "—"} testId="metric-delivery" />
          <MiniBox icon={CheckCircle2} label="Net Earned" value={formatEUR(m.net_completed_eur ?? 0)} testId="metric-earned" accent />
          <MiniBox icon={Activity} label="Revisions" value={m.revisions_received ?? 0} testId="metric-revisions" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Monthly chart */}
          {dash?.monthly_revenue?.length > 0 && (
            <Section title={isCompany ? "Spend · last 6 months" : "Revenue · last 6 months"}>
              <MonthlyChart data={isCompany ? dash.monthly_spend : dash.monthly_revenue} />
            </Section>
          )}

          <Section title="Active Orders">
            {activeOrders.length === 0 && <div className="text-xs text-[#606070]">No active orders.</div>}
            <div className="space-y-2">
              {activeOrders.slice(0, 5).map((o) => (
                <Link
                  key={o.id}
                  to={`/orders/${o.id}`}
                  data-testid={`dash-order-${o.id}`}
                  className="flex items-center justify-between gap-4 cc-elevated rounded-xl p-3.5 hover:border-[#1f1f3a] border border-[#15152a] transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[11px] text-[#00ff88]">{o.id}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{o.content_type} · {o.game}</div>
                      <div className="text-[11px] text-[#606070] truncate">
                        {isCompany ? o.creator_name : o.company_name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusPill status={o.status} />
                    <span className="font-mono text-sm text-[#00ff88]">${o.price}</span>
                  </div>
                </Link>
              ))}
            </div>
          </Section>

          {isCompany ? (
            <Section title="Top creators you've worked with">
              {(dash?.top_creators || []).length === 0 ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  {creators.map((c) => (
                    <Link key={c.id} to={`/creators/${c.id}`} className="cc-elevated rounded-xl p-4 cc-card-hover flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg overflow-hidden border border-[#15152a] bg-[#06060c]">
                        {c.avatar_url ? <img src={c.avatar_url} alt={c.username} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl text-[#00ff88]">{c.glyph}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{c.glyph} {c.username}</div>
                        <div className="text-[11px] text-[#606070] font-mono truncate">{formatNumber(c.subscribers)} · ${c.price}</div>
                      </div>
                      <Star size={12} className="fill-[#ffaa00] text-[#ffaa00]" />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {dash.top_creators.map((c) => (
                    <Link key={c.id} to={`/creators/${c.id}`} className="cc-elevated rounded-xl p-4 cc-card-hover flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg overflow-hidden border border-[#15152a] bg-[#06060c]">
                        {c.avatar_url ? <img src={c.avatar_url} alt={c.username} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl text-[#00ff88]">{c.glyph}</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{c.glyph} {c.username}</div>
                        <div className="text-[11px] text-[#606070] font-mono truncate">{c.orders_placed} order{c.orders_placed === 1 ? "" : "s"} · {(c.rating || 0).toFixed(1)}★</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Section>
          ) : (
            <Section title="Recent reviews">
              <div className="text-xs text-[#606070]">
                {(m.reviews_count ?? 0) === 0
                  ? "No reviews yet — your first delivery unlocks the rating board."
                  : `${m.avg_rating.toFixed(2)} ★ across ${m.reviews_count} reviews. Keep it up!`}
              </div>
            </Section>
          )}
        </div>

        <div className="space-y-6">
          <Section title="Recent messages">
            {convs.slice(0, 5).map((c) => (
              <button
                key={c.id}
                onClick={() => openChat(c)}
                data-testid={`dash-conv-${c.id}`}
                className="w-full text-left cc-elevated rounded-xl p-3 hover:border-[#1f1f3a] border border-[#15152a] mb-2 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#06060c] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
                    {isCompany ? (c.creator_glyph || "◈") : "◆"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{isCompany ? c.creator_name : c.company_name}</div>
                    <div className="text-[10px] text-[#606070] font-mono">{timeAgo(c.last_message_at)}</div>
                  </div>
                </div>
                <div className="text-[11px] text-[#a0a0b0] truncate mt-1">{c.last_message || "—"}</div>
              </button>
            ))}
            {convs.length === 0 && <div className="text-xs text-[#606070]">No messages.</div>}
          </Section>

          <Section title="Marketplace · global">
            <div className="space-y-2.5">
              <MiniStat icon={Users} label="Creators" value={formatNumber(stats?.creators ?? 0)} />
              <MiniStat icon={Briefcase} label="Delivered" value={formatNumber(stats?.orders_delivered ?? 0)} />
              <MiniStat icon={TrendingUp} label="Volume" value={formatEUR(stats?.volume_eur ?? 0)} accent />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="cc-surface rounded-2xl p-6">
    <div className="cc-overline mb-4">{title}</div>
    {children}
  </section>
);

const MiniStat = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3">
    <span className="w-8 h-8 rounded-md border border-[#15152a] bg-[#06060c] flex items-center justify-center text-[#00ff88]">
      <Icon size={14} />
    </span>
    <div className="flex-1 flex items-center justify-between">
      <span className="text-xs text-[#a0a0b0]">{label}</span>
      <span className={`font-mono text-sm ${accent ? "text-[#00ff88]" : "text-white"}`}>{value}</span>
    </div>
  </div>
);

const MiniBox = ({ icon: Icon, label, value, testId, accent }) => (
  <div data-testid={testId} className="cc-elevated rounded-xl p-4 border border-[#15152a]">
    <div className="flex items-center gap-2 text-[11px] text-[#606070] uppercase tracking-wider">
      <Icon size={12} className="text-[#00ff88]" /> {label}
    </div>
    <div className={`mt-1 font-mono text-lg ${accent ? "text-[#00ff88]" : "text-white"}`}>{value}</div>
  </div>
);

const MonthlyChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-2 h-32" data-testid="dashboard-monthly-chart">
      {data.map((d) => {
        const pct = (d.value / max) * 100;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1.5 group">
            <div className="text-[10px] font-mono text-[#606070] group-hover:text-[#00ff88] transition-colors">
              {d.value > 0 ? `$${d.value}` : "—"}
            </div>
            <div className="w-full bg-[#0a0a16] rounded-t-sm overflow-hidden" style={{ height: "calc(100% - 28px)" }}>
              <div
                className="w-full bg-gradient-to-t from-[#00ff88]/30 to-[#00ff88]/80 rounded-t-sm transition-all"
                style={{ height: `${Math.max(pct, d.value > 0 ? 4 : 0)}%`, marginTop: `${100 - Math.max(pct, d.value > 0 ? 4 : 0)}%` }}
              />
            </div>
            <div className="text-[10px] text-[#606070] font-mono">{d.month.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
};
